/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Company } from 'types/entities/Company'
import { CompanyContact } from 'types/entities/CompanyContact'
import {
    extractBusinessEmailDomain,
    getEmailDomainSearchToken,
} from 'utils/email/emailDomainUtils'

export interface EmailDomainAccountSuggestion {
    domain: string
    searchToken: string
    companies: Company[]
}

const resolveContactCompanies = (contact: CompanyContact): Company[] => {
    if (Array.isArray(contact.companies) && contact.companies.length > 0) {
        return contact.companies.filter((company) => Boolean(company?.id))
    }

    const withCompany = contact as CompanyContact & { company?: Company; company_name?: string }
    if (withCompany.company?.id) {
        return [withCompany.company]
    }

    if (contact.company_id) {
        return [
            {
                id: contact.company_id,
                name: withCompany.company_name ?? '',
            } as Company,
        ]
    }

    return []
}

/**
 * From contacts found via domain search, pick unique Accounts to suggest for linking.
 */
export const buildEmailDomainAccountSuggestions = (
    contactEmail: string | null | undefined,
    matchedContacts: CompanyContact[],
    options?: { excludeCompanyIds?: number[]; limit?: number }
): EmailDomainAccountSuggestion | null => {
    const domain = extractBusinessEmailDomain(contactEmail)
    const searchToken = getEmailDomainSearchToken(contactEmail)
    if (!domain || !searchToken) {
        return null
    }

    const exclude = new Set(options?.excludeCompanyIds ?? [])
    const limit = options?.limit ?? 5
    const byId = new Map<number, Company>()

    for (const matched of matchedContacts) {
        const matchedDomain = extractBusinessEmailDomain(matched.email)
        if (matchedDomain !== domain) {
            continue
        }

        for (const company of resolveContactCompanies(matched)) {
            if (!company?.id || exclude.has(company.id) || byId.has(company.id)) {
                continue
            }
            byId.set(company.id, company)
            if (byId.size >= limit) {
                break
            }
        }

        if (byId.size >= limit) {
            break
        }
    }

    if (byId.size === 0) {
        return null
    }

    return {
        domain,
        searchToken,
        companies: [...byId.values()],
    }
}
